import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ApiError, del, get, post, put, type Programme, type StoryDto } from '../api.js';
import { PageTitle } from '../frame.js';
import { t, currentLocale } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { navigate, useLocation } from '../router.js';
import { useSession } from '../session.js';
import { StoryStatusBadge } from '../status-badge.js';
import { fmtDateTime, hhmmss, mmss, parseMmSs, todayIso, fmtDayDate } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';
import { SPELL_CLICK_EVENT, collectText, setSpellRanges, spellKey, spellPlugin, toRanges, type SpellClick, type SpellRange } from '../spell-plugin.js';
import { AiPanel, aiStatus, type AiStatus } from './ai-panel.js';
import { MediaDialog } from './rundown-dialogs.js';
import { CG_TEMPLATES, boxKeys, cgTemplate, parseStoryline, rekeyStorylineAt, renderStoryline, storylineAnchors, storylineCg, strapDesignFor, type StorylineBlock } from '@airpost/shared';
import { StrapDesignContext } from '../strap-view.js';
import { STORYBOARD_EVENT, currentSlots, sameSlots, setStoryboardCues, storyboardKey, storyboardPlugin, type CueSlot, type SlotAction, type SlotLabels } from '../storyboard-plugin.js';
import { PreviewDialog, StoryboardPanel, orderCues, templateName, toDraft, type CueDraft, type CueDto } from './storyboard-panel.js';
import { StorylinePanel, type StorylineCueAction } from './storyline-panel.js';

type SaveState = 'local' | 'pending' | 'saved' | 'error' | 'conflict';
const ITEM_KINDS = ['story', 'separator', 'break', 'studio', 'live', 'phone', 'vo', 'package', 'graphics', 'filler', 'other'];
const DRAFT_PREFIX = 'airpost.draft.';

interface Fields {
  title: string;
  displayNumber: string;
  categoryId: string;
  originUserId: string;
  guideComments: string;
  videoS: string;
  readOverrideS: string;
  kind: string;
}

export function StoryEditorPage({ id }: { id: string | null }) {
  const { session, can } = useSession();
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const { query } = useLocation();
  const rundownId = query.get('rundownId');
  const feedItemId = query.get('feedItem');
  const from = query.get('from');
  const [story, setStory] = useState<StoryDto | null>(null);
  const [meta, setMeta] = useState<{ createdBy: string | null; updatedBy: string | null; canEdit: boolean; placements: Array<{ rundownId: string; programmeName: string; date: string }> } | null>(null);
  const [fields, setFields] = useState<Fields>({ title: '', displayNumber: '', categoryId: '', originUserId: session?.user.id ?? '', guideComments: '', videoS: '00:00', readOverrideS: '', kind: 'story' });
  /** Boxes inside boxes: box key → the second it comes in inside the box before it (titles keep theirs on their cue). */
  const [storylineAt, setStorylineAt] = useState<Record<string, number>>({});
  const [programmeId, setProgrammeId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: string; displayName: string }>>([]);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  /** "Αποθηκεύτηκε" for a moment after Αποθήκευση (the button or Ctrl+S); the value restarts it on every save. */
  const [savedPop, setSavedPop] = useState(0);
  const [conflict, setConflict] = useState<StoryDto | null>(null);
  const [lease, setLease] = useState<{ ok: boolean; holder?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState(0);
  // orthographer and AI help
  const [spellOn, setSpellOn] = useState<boolean>(() => localStorage.getItem('airpost.spell') !== '0');
  const [spellCount, setSpellCount] = useState(0);
  const [spellPopup, setSpellPopup] = useState<SpellClick | null>(null);
  const [spellTick, setSpellTick] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [ai, setAi] = useState<AiStatus | null>(null);
  // the storyboard: the cues of the story (clips or virtual codes, CG titles) in playing order
  const [cues, setCues] = useState<CueDto[]>([]);
  const [slots, setSlots] = useState<CueSlot[]>([]);
  const [sbOpen, setSbOpen] = useState<boolean>(() => localStorage.getItem('airpost.storyboard') !== '0');
  const [sbBusy, setSbBusy] = useState(false);
  const [sbNotice, setSbNotice] = useState<string | null>(null);
  const [sbFocus, setSbFocus] = useState<string | null>(null);
  const [preview, setPreview] = useState<CueDto | null>(null);
  const [chooser, setChooser] = useState<{ cue: CueDto | null; slot: CueSlot | null } | null>(null);
  // the storyline: the script as boxes; the editor's document is rendered from them while this view is on
  const [mode, setMode] = useState<'storyline' | 'text'>(() => (localStorage.getItem('airpost.storyline') === 'text' ? 'text' : 'storyline'));
  const [blocks, setBlocks] = useState<StorylineBlock[]>([]);
  const ignoredWords = useRef(new Set<string>());
  const spellTimer = useRef<number | null>(null);
  const spellOnRef = useRef(spellOn);
  const editorRef = useRef<Editor | null>(null);
  const modeRef = useRef(mode);
  const dirty = useRef(false);
  const timer = useRef<number | null>(null);
  const charsSinceSave = useRef(0);
  const revisionRef = useRef(0);
  const storyIdRef = useRef<string | null>(id);

  const editor = useEditor({
    extensions: [StarterKit],
    content: '',
    editorProps: { attributes: { spellcheck: 'true', lang: 'el' } },
    onUpdate: ({ editor }) => {
      dirty.current = true;
      charsSinceSave.current += 1;
      setWordCount(countWords(editor.getText()));
      setSaveState('local');
      scheduleSave();
      scheduleSpell();
    },
  });

  const countWords = (s: string) => (s.replace(/ /g, ' ').match(/\S+/g) ?? []).length;

  // ── orthographer ──────────────────────────────────────────────────────────────
  useEffect(() => {
    spellOnRef.current = spellOn;
  }, [spellOn]);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  /** Read the boxes out of the document (after a load, a revert, a text the AI or a feed wrote). */
  const syncBlocks = useCallback(() => {
    const ed = editorRef.current;
    if (ed) setBlocks(parseStoryline(ed.getText({ blockSeparator: '\n' })));
  }, []);
  useEffect(() => {
    void aiStatus().then(setAi);
  }, []);
  const runSpell = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed || !spellOnRef.current) return;
    const { text, segments } = collectText(ed.state.doc);
    if (!text.trim()) {
      setSpellRanges(ed.view, []);
      setSpellCount(0);
      return;
    }
    try {
      const r = await post<{ misspelled: Array<{ word: string; offset: number; length: number; suggestions: string[] }> }>('/api/spell/check', { text });
      const now = editorRef.current;
      if (!now || !spellOnRef.current) return;
      if (collectText(now.state.doc).text !== text) return; // changed meanwhile; the next check covers it
      const ranges = toRanges(r.misspelled, segments, ignoredWords.current);
      setSpellRanges(now.view, ranges);
      setSpellCount(ranges.length);
    } catch {
      // the checker is unavailable: the text is left alone
    }
  }, []);
  const scheduleSpell = useCallback(() => {
    if (spellTimer.current) window.clearTimeout(spellTimer.current);
    spellTimer.current = window.setTimeout(() => void runSpell(), 900);
  }, [runSpell]);
  useEffect(() => {
    if (!editor) return;
    if (!spellOn) return;
    editor.registerPlugin(spellPlugin());
    void runSpell();
    return () => {
      editor.unregisterPlugin(spellKey);
    };
  }, [editor, spellOn, runSpell]);
  useEffect(() => {
    if (spellTick) scheduleSpell();
  }, [spellTick, scheduleSpell]);
  useEffect(() => {
    const onClick = (e: Event) => setSpellPopup((e as CustomEvent<SpellClick>).detail);
    const onAway = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('.spell-popup')) setSpellPopup(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSpellPopup(null);
    window.addEventListener(SPELL_CLICK_EVENT, onClick);
    document.addEventListener('mousedown', onAway);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(SPELL_CLICK_EVENT, onClick);
      document.removeEventListener('mousedown', onAway);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  const toggleSpell = () => {
    const v = !spellOn;
    setSpellOn(v);
    setSpellCount(0);
    localStorage.setItem('airpost.spell', v ? '1' : '0');
  };
  const replaceWord = (r: SpellRange, word: string) => {
    editor?.chain().focus().insertContentAt({ from: r.from, to: r.to }, word).run();
    setSpellPopup(null);
    scheduleSpell();
  };
  const ignoreWord = (word: string) => {
    ignoredWords.current.add(word);
    setSpellPopup(null);
    void runSpell();
  };
  const addToDictionary = async (word: string) => {
    try {
      await post('/api/spell/words', { word });
    } catch {
      // no right to add: kept out of this session's list only
    }
    ignoredWords.current.add(word);
    setSpellPopup(null);
    void runSpell();
  };

  // ── a new story from an incoming feed item: title, summary and the source line ────
  useEffect(() => {
    if (id || !feedItemId || !editor) return;
    let cancelled = false;
    get<{ item: { title: string; summary: string; body: string | null; link: string | null; sourceName: string; author: string | null } }>(`/api/feeds/items/${feedItemId}`)
      .then((r) => {
        if (cancelled) return;
        const it = r.item;
        setFields((f) => ({ ...f, title: f.title || it.title }));
        const esc = (x: string) => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const source = `${t('feedsSourceLine')}: ${it.sourceName}${it.author ? `, ${it.author}` : ''}${it.link ? ` — ${it.link}` : ''}`;
        const text = (it.body ?? it.summary)
          .split(/\n\n+/)
          .map((x) => `<p>${esc(x)}</p>`)
          .join('');
        editor.commands.setContent(`${text}<p><em>${esc(source)}</em></p>`, { emitUpdate: true });
        syncBlocks();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id, feedItemId, editor, syncBlocks]);

  // ── AI help: proposals in and out of the editor as plain text ──────────────────
  const editorPlainText = () => editor?.getText({ blockSeparator: '\n\n' }) ?? '';
  const applyAiText = (text: string) => {
    if (!editor) return;
    const esc = (x: string) => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = text
      .trim()
      .split(/\n{2,}/)
      .map((para) => `<p>${esc(para).split('\n').join('<br>')}</p>`)
      .join('');
    editor.chain().focus().setContent(html, { emitUpdate: true }).run();
    syncBlocks();
  };

  // ── load ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    get<{ programmes: Programme[] }>('/api/programmes').then((r) => setProgrammes(r.programmes));
    get<{ categories: Array<{ id: string; name: string }> }>('/api/categories').then((r) => setCategories(r.categories));
    get<{ users: Array<{ id: string; displayName: string }> }>('/api/users').then((r) => setUsers(r.users));
  }, []);

  useEffect(() => {
    if (!id || !editor) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await get<{ story: StoryDto; createdBy: string | null; updatedBy: string | null; canEdit: boolean; placements: never }>(`/api/stories/${id}`);
        if (cancelled) return;
        setStory(r.story);
        revisionRef.current = r.story.revision;
        void get<{ cues: CueDto[] }>(`/api/stories/${id}/storyboard`)
          .then((x) => !cancelled && setCues(x.cues))
          .catch(() => undefined);
        setMeta({ createdBy: r.createdBy, updatedBy: r.updatedBy, canEdit: r.canEdit, placements: r.placements });
        setFields({ title: r.story.title, displayNumber: r.story.displayNumber ?? '', categoryId: r.story.categoryId ?? '', originUserId: r.story.originUserId ?? '', guideComments: r.story.guideComments ?? '', videoS: mmss(r.story.videoS), readOverrideS: r.story.readOverrideS === null ? '' : mmss(r.story.readOverrideS), kind: r.story.kind });
        setStorylineAt(r.story.storylineAt ?? {});
        editor.commands.setContent(r.story.bodyHtml || '<p></p>', { emitUpdate: false });
        syncBlocks();
        setWordCount(r.story.wordCount);
        setSpellTick((n) => n + 1);
        setSavedAt(r.story.updatedAt);
        // a local draft newer than the server copy?
        const draft = readDraft(id);
        if (draft && draft.revision === r.story.revision && draft.html !== r.story.bodyHtml) setSaveState('local');
        // lease
        if (r.canEdit) {
          try {
            await post('/api/leases', { resourceType: 'story', resourceId: id });
            setLease({ ok: true });
            realtime.setEditing(id);
          } catch (e) {
            const p = e instanceof ApiError ? (e.payload as { holder?: { displayName: string } }) : {};
            setLease({ ok: false, holder: p.holder?.displayName });
          }
        } else setLease({ ok: false });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, editor, syncBlocks]);

  // lease heartbeat and release
  useEffect(() => {
    if (!id || !lease?.ok) return;
    const h = window.setInterval(() => void post('/api/leases', { resourceType: 'story', resourceId: id }).catch(() => {}), 45_000);
    return () => {
      window.clearInterval(h);
      void del(`/api/leases/story/${id}`).catch(() => {});
      realtime.setEditing(null);
    };
  }, [id, lease?.ok]);

  // other users' saves of this story
  useEffect(() => {
    return realtime.listen((m: WsMessage) => {
      if (m.type === 'story' && (m.story as StoryDto).id === storyIdRef.current) {
        const s = m.story as StoryDto;
        if (s.revision > revisionRef.current && s.updatedById !== session?.user.id) setConflict(s);
      }
    });
  }, [session?.user.id]);

  // ── save ──────────────────────────────────────────────────────────────────────
  const payload = useCallback(
    () => ({
      title: fields.title,
      bodyHtml: editor?.getHTML() ?? '',
      displayNumber: fields.displayNumber.trim() === '' ? null : fields.displayNumber.trim(),
      categoryId: fields.categoryId || null,
      originUserId: fields.originUserId || null,
      guideComments: fields.guideComments || null,
      videoS: parseMmSs(fields.videoS) ?? 0,
      readOverrideS: fields.readOverrideS.trim() === '' ? null : (parseMmSs(fields.readOverrideS) ?? null),
      kind: fields.kind,
      storylineAt,
    }),
    [fields, editor, storylineAt],
  );

  const save = useCallback(
    async (opts: { explicit?: boolean; announce?: boolean } = {}) => {
      if (!editor) return;
      if (!fields.title.trim()) {
        if (opts.explicit) setError('Ο Τίτλος είναι υποχρεωτικός');
        return;
      }
      setSaveState('pending');
      setError(null);
      try {
        if (!storyIdRef.current) {
          if (!programmeId && !rundownId) {
            if (opts.explicit) setError('Το Δελτίο είναι υποχρεωτικό');
            setSaveState('local');
            return;
          }
          const r = await post<{ story: StoryDto; rundownId: string | null }>('/api/stories', { ...payload(), rundownId: rundownId ?? undefined, programmeId: rundownId ? undefined : programmeId, date: rundownId ? undefined : date });
          storyIdRef.current = r.story.id;
          revisionRef.current = r.story.revision;
          setStory(r.story);
          navigate(`/story/${r.story.id}/edit`, { replace: true });
        } else {
          const r = await put<{ story: StoryDto; applied: boolean; demoted?: boolean }>(`/api/stories/${storyIdRef.current}`, { ...payload(), baseRevision: revisionRef.current, saveId: crypto.randomUUID() });
          revisionRef.current = r.story.revision;
          setStory(r.story);
          // the change made a finalised story a draft again: say so once
          if (r.demoted) setStatusNote(t('demotedNote'));
        }
        dirty.current = false;
        charsSinceSave.current = 0;
        setSaveState('saved');
        setSavedAt(new Date().toISOString());
        if (opts.announce) setSavedPop(Date.now());
        if (storyIdRef.current) clearDraft(storyIdRef.current);
      } catch (e) {
        if (e instanceof ApiError && e.code === 'CONFLICT') {
          setConflict((e.payload as { current: StoryDto }).current);
          setSaveState('conflict');
        } else {
          setSaveState('error');
          setError(e instanceof ApiError ? e.message : String(e));
        }
        if (storyIdRef.current) writeDraft(storyIdRef.current, { html: editor.getHTML(), revision: revisionRef.current, at: Date.now() });
      }
    },
    [editor, fields.title, payload, programmeId, date, rundownId],
  );

  useEffect(() => {
    if (!savedPop) return;
    const h = window.setTimeout(() => setSavedPop(0), 1800);
    return () => window.clearTimeout(h);
  }, [savedPop]);

  const scheduleSave = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    // HPress: frequent autosave; here 2 s after the last keystroke, or at once after 50 characters
    const delay = charsSinceSave.current >= 50 ? 0 : 2000;
    timer.current = window.setTimeout(() => void save(), delay);
    if (storyIdRef.current && editor) writeDraft(storyIdRef.current, { html: editor.getHTML(), revision: revisionRef.current, at: Date.now() });
  }, [save, editor]);

  // the map as the story was loaded: a box put inside another (or taken out) is a change to save
  const loadedAt = useRef<string | null>(null);
  useEffect(() => {
    if (!story) return;
    const now = JSON.stringify(storylineAt);
    if (loadedAt.current === null || loadedAt.current === now) {
      loadedAt.current = now;
      return;
    }
    loadedAt.current = now;
    dirty.current = true;
    scheduleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storylineAt]);
  // the fields as the story was loaded: filling them in on open is not a change to save
  const loadedFields = useRef<string | null>(null);
  useEffect(() => {
    if (!story) return;
    const now = JSON.stringify(fields);
    if (loadedFields.current === null || loadedFields.current === now) {
      loadedFields.current = now;
      return;
    }
    loadedFields.current = now;
    dirty.current = true;
    scheduleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void save({ explicit: true, announce: true });
      }
    };
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [save]);

  const readOnly = !!lease && !lease.ok;
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  // ── the storyboard ─────────────────────────────────────────────────────────────
  const savedStoryId = story?.id ?? id;
  const locale = session?.user.locale ?? 'el';
  const sbLabels = useMemo<SlotLabels>(
    () => ({ choose: t('storyVideoChoose'), virtual: t('storyVideoVirtual'), addCg: t('cueAddCg'), preview: t('cuePreview'), edit: t('cueEdit'), remove: t('storyVideoRemove'), waiting: t('virtualWaiting'), templateName: (k) => templateName(k, locale) }),
    [locale],
  );
  const cuesRef = useRef<CueDto[]>([]);
  useEffect(() => {
    cuesRef.current = cues;
  }, [cues]);
  useEffect(() => {
    if (!editor) return;
    editor.registerPlugin(storyboardPlugin(sbLabels, readOnly));
    // a fresh plugin starts empty: hand it the cues we already hold
    setStoryboardCues(editor.view, cuesRef.current, readOnly);
    const sync = () =>
      setSlots((prev) => {
        const next = currentSlots(editor.state);
        return sameSlots(prev, next) ? prev : next;
      });
    sync();
    editor.on('transaction', sync);
    return () => {
      editor.off('transaction', sync);
      editor.unregisterPlugin(storyboardKey);
    };
  }, [editor, sbLabels, readOnly]);
  useEffect(() => {
    if (editor) setStoryboardCues(editor.view, cues, readOnly);
  }, [editor, cues, readOnly]);
  const commitCues = useCallback(
    async (next: CueDraft[]): Promise<boolean> => {
      let sid = storyIdRef.current;
      if (!sid) {
        await save({ explicit: true });
        sid = storyIdRef.current;
      }
      if (!sid) {
        setSbNotice(t('storyVideoSaveFirst'));
        return false;
      }
      setSbBusy(true);
      try {
        const r = await put<{ cues: CueDto[]; story: StoryDto; rows: number }>(`/api/stories/${sid}/storyboard`, { cues: next });
        setCues(r.cues);
        setStory((s) => (s ? { ...s, mediaAssetId: r.story.mediaAssetId, mediaCode: r.story.mediaCode } : r.story));
        setSbNotice(r.rows ? `${r.rows} ${t('storyVideoRows')}` : t('storyboardSaved'));
        return true;
      } catch (e) {
        setSbNotice(e instanceof ApiError ? e.message : String(e));
        return false;
      } finally {
        setSbBusy(false);
      }
    },
    [save],
  );
  const onSlotAction = useCallback(
    (a: SlotAction) => {
      const list = orderCues(cues, slots).map(toDraft);
      const { slot, cue } = a;
      const full = cue ? (cues.find((c) => c.id === cue.id) ?? null) : null;
      switch (a.action) {
        case 'preview':
          if (full?.mediaAssetId) setPreview(full);
          break;
        case 'choose':
          setChooser({ cue: full, slot });
          break;
        case 'virtual':
          void commitCues(full ? list.map((c) => (c.id === full.id ? { ...c, mediaAssetId: null, mediaCode: 'auto' } : c)) : [...list, { kind: 'video', anchor: slot.key, label: slot.label, mediaAssetId: null, mediaCode: 'auto', template: null, lines: [], durationMs: null }]);
          break;
        case 'cg': {
          const tpl = cgTemplate(slot.template) ?? CG_TEMPLATES[0];
          void commitCues([...list, { kind: 'cg', anchor: slot.key, label: slot.label, mediaAssetId: null, mediaCode: null, template: tpl.key, lines: slot.following.slice(0, tpl.lines), durationMs: tpl.defaultMs }]);
          break;
        }
        case 'edit':
          setSbOpen(true);
          setSbFocus(full?.id ?? null);
          break;
        case 'remove':
          if (full) void commitCues(list.filter((c) => c.id !== full.id));
          break;
      }
    },
    [cues, slots, commitCues],
  );
  useEffect(() => {
    const h = (e: Event) => onSlotAction((e as CustomEvent<SlotAction>).detail);
    window.addEventListener(STORYBOARD_EVENT, h);
    return () => window.removeEventListener(STORYBOARD_EVENT, h);
  }, [onSlotAction]);
  const toggleStoryboard = () =>
    setSbOpen((o) => {
      localStorage.setItem('airpost.storyboard', o ? '0' : '1');
      return !o;
    });
  const assignClip = async (mediaAssetId: string | null) => {
    if (!chooser) return;
    const list = orderCues(cues, slots).map(toDraft);
    const target = chooser.cue;
    if (target) await commitCues(mediaAssetId ? list.map((c) => (c.id === target.id ? { ...c, mediaAssetId } : c)) : list.filter((c) => c.id !== target.id));
    else if (mediaAssetId) await commitCues([...list, { kind: 'video', anchor: chooser.slot?.key ?? null, label: chooser.slot?.label ?? t('storyVideo'), mediaAssetId, mediaCode: null, template: null, lines: [], durationMs: null }]);
    setChooser(null);
  };

  // ── the storyline ──────────────────────────────────────────────────────────────
  const anchors = useMemo(() => storylineAnchors(blocks), [blocks]);
  const updateBlocks = (next: StorylineBlock[], then?: StorylineCueAction) => {
    let list = orderCues(cues, slots).map(toDraft);
    let changed = false;
    const renamed = reanchored(blocks, next, list);
    if (renamed) {
      list = renamed;
      changed = true;
    }
    if (then) {
      const r = cueDraftsAfter(then, next, list);
      if (r) {
        list = r;
        changed = true;
      }
    }
    // the seconds of the boxes inside boxes follow their boxes; a box put inside (or taken out) sets its own
    let at = rekeyStorylineAt(blocks, next, storylineAt);
    if (then?.action === 'ride') at = withRide(at, boxKeys(next)[then.index] ?? null, then.atMs);
    if (JSON.stringify(at) !== JSON.stringify(storylineAt)) setStorylineAt(at);
    setBlocks(next);
    editor?.commands.setContent(renderStoryline(next).html, { emitUpdate: true });
    if (changed) void commitCues(list);
  };
  /** The map with box `key` at a second inside the box before it, or a step of its own (null). */
  const withRide = (map: Record<string, number>, key: string | null, atMs: number | null): Record<string, number> => {
    if (!key) return map;
    const out = { ...map };
    if (atMs === null) delete out[key];
    else out[key] = Math.max(0, Math.round(atMs));
    return out;
  };
  /**
   * A cue action that needs only the boxes: a title's time on the box before it (`at`). The drafts after it, or null
   * when nothing changes; a title without a CG cue yet gets one with the time.
   */
  const cueDraftsAfter = (a: StorylineCueAction, blocksNow: StorylineBlock[], list: CueDraft[]): CueDraft[] | null => {
    if (a.action !== 'at') return null;
    const anchor = storylineAnchors(blocksNow)[a.index];
    const b = blocksNow[a.index];
    if (!anchor || !b) return null;
    const existing = list.find((c) => c.anchor === anchor);
    if (existing) return existing.inMs === a.atMs ? null : list.map((c) => (c === existing ? { ...c, inMs: a.atMs } : c));
    if (a.atMs === null) return null;
    const cg = storylineCg(b);
    if (!cg) return null;
    const tpl = cgTemplate(cg.template) ?? CG_TEMPLATES[0];
    return [...list, { kind: 'cg', anchor, label: anchor.split('#')[0]!, mediaAssetId: null, mediaCode: null, inMs: a.atMs, outMs: null, template: tpl.key, lines: cg.lines.slice(0, tpl.lines), durationMs: tpl.defaultMs }];
  };
  /**
   * The cues follow their boxes: a box that moves (its anchor renumbers) or is edited in place takes its cue's anchor
   * along, and the cue of a removed box is left unanchored. Boxes are matched by identity (the panel keeps the objects
   * it does not touch), an edited box by its position.
   */
  const reanchored = (from: StorylineBlock[], to: StorylineBlock[], list: CueDraft[]): CueDraft[] | null => {
    if (!list.length) return null;
    const before = storylineAnchors(from);
    const after = storylineAnchors(to);
    const renames = new Map<string, string | null>();
    const matched = new Set<number>();
    to.forEach((b, k) => {
      let i = from.indexOf(b);
      if (i < 0 && from.length === to.length && from[k]!.kind === b.kind) i = k;
      if (i < 0 || matched.has(i)) return;
      matched.add(i);
      if (before[i] && before[i] !== after[k]) renames.set(before[i]!, after[k] ?? null);
    });
    from.forEach((_, i) => {
      if (!matched.has(i) && before[i]) renames.set(before[i]!, null);
    });
    if (!list.some((c) => c.anchor && renames.has(c.anchor))) return null;
    return list.map((c) => (c.anchor && renames.has(c.anchor) ? { ...c, anchor: renames.get(c.anchor) ?? null } : c));
  };
  const switchMode = (m: 'storyline' | 'text') => {
    if (m === 'storyline') syncBlocks();
    setMode(m);
    localStorage.setItem('airpost.storyline', m);
  };
  /** Μοντάζ of a clip: the story must exist first (it gets saved), then the editor page opens on (story, clip). */
  const openEdit = async (mediaAssetId: string) => {
    let sid = storyIdRef.current;
    if (!sid) {
      await save({ explicit: true });
      sid = storyIdRef.current;
    }
    if (!sid) {
      setSbNotice(t('storyVideoSaveFirst'));
      return;
    }
    navigate(`/edit/new?story=${sid}&clip=${encodeURIComponent(mediaAssetId)}`);
  };
  const onStorylineCue = (a: StorylineCueAction) => {
    if (a.action === 'ride') {
      setStorylineAt((m) => withRide(m, boxKeys(blocks)[a.index] ?? null, a.atMs));
      return;
    }
    if (a.action === 'at') {
      const r = cueDraftsAfter(a, blocks, orderCues(cues, slots).map(toDraft));
      if (r) void commitCues(r);
      return;
    }
    const anchor = anchors[a.index];
    const b = blocks[a.index];
    if (!anchor || !b) return;
    const list = orderCues(cues, slots).map(toDraft);
    const existing = cues.find((c) => c.anchor === anchor) ?? null;
    const label = anchor.split('#')[0]!;
    const slot: CueSlot = slots.find((s) => s.key === anchor) ?? { key: anchor, kind: b.kind === 'video' ? 'video' : 'cg', template: storylineCg(b)?.template ?? null, label, pos: 0, following: [] };
    switch (a.action) {
      case 'choose':
        setChooser({ cue: existing, slot });
        break;
      case 'virtual':
        void commitCues(existing ? list.map((c) => (c.id === existing.id ? { ...c, mediaAssetId: null, mediaCode: 'auto' } : c)) : [...list, { kind: 'video', anchor, label, mediaAssetId: null, mediaCode: 'auto', template: null, lines: [], durationMs: null }]);
        break;
      case 'preview':
        if (existing?.mediaAssetId) setPreview(existing);
        break;
      case 'edit':
        if (!existing?.mediaAssetId) break;
        if (existing.edit) navigate(`/edit/${existing.edit.id}`);
        else void openEdit(existing.mediaAssetId);
        break;
      case 'removeCue':
        if (existing) void commitCues(list.filter((c) => c.id !== existing.id));
        break;
      case 'addCg': {
        const cg = storylineCg(b);
        if (!cg) return;
        const tpl = cgTemplate(cg.template) ?? CG_TEMPLATES[0];
        void commitCues([...list, { kind: 'cg', anchor, label, mediaAssetId: null, mediaCode: null, template: tpl.key, lines: cg.lines.slice(0, tpl.lines), durationMs: tpl.defaultMs }]);
        break;
      }
      case 'cg':
        if (existing) void commitCues(list.map((c) => (c.id === existing.id ? { ...c, template: a.template, durationMs: a.durationMs } : c)));
        break;
    }
  };
  /** A title's text changed: its CG cue follows (a second line asks for the two-line strap). */
  const flushStorylineCg = (i: number) => {
    const anchor = anchors[i];
    const b = blocks[i];
    const cg = b ? storylineCg(b) : null;
    const existing = anchor ? cues.find((c) => c.anchor === anchor) : null;
    if (!existing || !cg || existing.kind !== 'cg') return;
    const template = existing.template?.startsWith('lower-third') && cg.template.startsWith('lower-third') ? cg.template : (existing.template ?? cg.template);
    const lines = cg.lines.slice(0, cgTemplate(template)?.lines ?? 2);
    if (template === existing.template && lines.join('\n') === existing.lines.filter(Boolean).join('\n')) return;
    const list = orderCues(cues, slots).map(toDraft);
    void commitCues(list.map((c) => (c.id === existing.id ? { ...c, template, lines } : c)));
  };

  const insertCue = async () => {
    // department template text ("Cue"); the admin page stores it; empty = HPress default of a blank template
    const r = await get<{ bodyHtml: string }>('/api/cue-template').catch(() => ({ bodyHtml: '' }));
    editor?.chain().focus().insertContent(r.bodyHtml || '<p>ON</p><p></p><p>ΦΑΣΑ:</p>').run();
  };
  const saveTemp = () => {
    if (!editor) return;
    localStorage.setItem(DRAFT_PREFIX + 'temp.' + session?.user.id, editor.getHTML());
    setError(null);
  };
  const insertTemp = () => {
    const html = localStorage.getItem(DRAFT_PREFIX + 'temp.' + session?.user.id);
    if (html) editor?.chain().focus().insertContent(html).run();
  };
  const revert = () => {
    if (!story) return;
    setFields({ title: story.title, displayNumber: story.displayNumber ?? '', categoryId: story.categoryId ?? '', originUserId: story.originUserId ?? '', guideComments: story.guideComments ?? '', videoS: mmss(story.videoS), readOverrideS: story.readOverrideS === null ? '' : mmss(story.readOverrideS), kind: story.kind });
    setStorylineAt(story.storylineAt ?? {});
    editor?.commands.setContent(story.bodyHtml || '<p></p>', { emitUpdate: false });
    syncBlocks();
    dirty.current = false;
    setSaveState('saved');
  };
  const takeTheirs = () => {
    if (!conflict) return;
    setStory(conflict);
    revisionRef.current = conflict.revision;
    setFields({ title: conflict.title, displayNumber: conflict.displayNumber ?? '', categoryId: conflict.categoryId ?? '', originUserId: conflict.originUserId ?? '', guideComments: conflict.guideComments ?? '', videoS: mmss(conflict.videoS), readOverrideS: conflict.readOverrideS === null ? '' : mmss(conflict.readOverrideS), kind: conflict.kind });
    setStorylineAt(conflict.storylineAt ?? {});
    editor?.commands.setContent(conflict.bodyHtml || '<p></p>', { emitUpdate: false });
    syncBlocks();
    setConflict(null);
    dirty.current = false;
    setSaveState('saved');
  };
  const keepMine = () => {
    if (!conflict) return;
    revisionRef.current = conflict.revision; // rebase my text on their revision and save over it (a new revision; theirs stays in Ιστορικό)
    setConflict(null);
    void save({ explicit: true });
  };

  const computedRead = useMemo(() => Math.round(wordCount / 3.3), [wordCount]);
  const stateLabel: Record<SaveState, string> = { local: t('stateLocal'), pending: t('statePending'), saved: t('stateSaved'), error: t('stateError'), conflict: t('stateConflict') };
  const approver = can('story.edit.any');
  /** The story's state: a draft until the journalist finalises it; the editor-in-chief approves. */
  const setStatus = async (status: 'draft' | 'ready' | 'approved') => {
    if (!storyIdRef.current) return;
    if (dirty.current) await save({ explicit: true });
    try {
      const r = await put<{ story: StoryDto }>(`/api/stories/${storyIdRef.current}/status`, { status });
      setStory(r.story);
      setStatusNote(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const toolbar = (
    <div className="toolbar">
      <button className="btn" onClick={() => (storyIdRef.current ? navigate(`/story/${storyIdRef.current}${from ? `?from=${from}` : ''}`) : window.history.back())}>
        ← {t('back')}
      </button>
      <button className="btn" onClick={() => void insertCue()} disabled={readOnly || mode === 'storyline'}>
        {t('cue')}
      </button>
      <button className="btn" onClick={insertTemp} disabled={readOnly || mode === 'storyline'}>
        {t('insertTemp')}
      </button>
      <button className="btn" onClick={saveTemp}>
        {t('saveTemp')}
      </button>
      <button className="btn" onClick={revert} disabled={!story}>
        ↺ {t('revert')}
      </button>
      <button className="btn primary" onClick={() => void save({ explicit: true, announce: true })} disabled={readOnly} title="Ctrl+S">
        💾 {t('save')}
      </button>
      {story && story.status === 'draft' && (
        <button className="btn primary" onClick={() => void setStatus('ready')} disabled={readOnly} title={t('finaliseHint')}>
          ✔ {t('finalise')}
        </button>
      )}
      {story && story.status === 'ready' && approver && (
        <button className="btn primary" onClick={() => void setStatus('approved')} disabled={readOnly} title={t('approveHint')}>
          ✔ {t('approve')}
        </button>
      )}
    </div>
  );

  if (error && !story && id) return <div className="banner err">{error}</div>;
  const placements = meta?.placements ?? [];
  // the story's date: its bulletin's (the first one when it is in several), else the day it was written
  const storyDay = placements[0] ? fmtDayDate(placements[0].date, currentLocale()) : story ? fmtDayDate(story.createdAt.slice(0, 10), currentLocale()) : '';
  // the lower thirds preview in the design of the bulletin the story was opened from, else its latest bulletin's
  const strapDesignKey = strapDesignFor((placements.find((p) => p.rundownId === rundownId) ?? placements[0])?.programmeName).key;
  return (
    <StrapDesignContext.Provider value={strapDesignKey}>
      <PageTitle title={id ? `${t('storyEdit')}${storyDay ? ` · ${storyDay}` : ''} (${t('author')}: ${meta?.createdBy ?? '…'}, ${t('department')}: TV)` : t('storyNew')} hint={id ? "Τροποποιήστε τα στοιχεία της είδησης και πατήστε 'Αποθήκευση'" : 'Συμπληρώστε τα στοιχεία της είδησης και το κείμενο'} />
      {id && story && (
        <div className="story-dates">
          <span className="story-date-main">📅 {storyDay}</span>
          {placements.length > 0 && (
            <span>
              {t('bulletin')}: <b>{placements.map((p) => p.programmeName).join(', ')}</b>
            </span>
          )}
          <span>
            {t('createdAt')}: <b>{fmtDateTime(story.createdAt)}</b>
          </span>
          <span>
            {t('updatedAt')}: <b>{fmtDateTime(story.updatedAt)}</b>
          </span>
        </div>
      )}
      {toolbar}
      {readOnly && (
        <div className="banner warn">
          {t('readOnlyNow')}: {lease?.holder ? `${t('leasedBy')} ${lease.holder}` : 'δεν έχετε δικαίωμα τροποποίησης αυτής της είδησης'}
        </div>
      )}
      {conflict && (
        <div className="banner err">
          <span>
            {t('stateConflict')} <b>{users.find((u) => u.id === conflict.updatedById)?.displayName ?? '—'}</b> ({t('revision')} {conflict.revision}).
          </span>
          <button className="btn" onClick={keepMine}>
            {t('keepMine')}
          </button>
          <button className="btn" onClick={takeTheirs}>
            {t('keepTheirs')}
          </button>
        </div>
      )}
      <div className="editor-fields">
        <label className="req">{t('bulletinDate')} :</label>
        {id || rundownId ? <span className="story-date-field">{placements.length ? [...new Set(placements.map((p) => fmtDayDate(p.date, currentLocale())))].join(', ') : rundownId ? '(από τον Οδηγό)' : '—'}</span> : <DateInput value={date} onChange={setDate} />}
        <label>{t('origin')} :</label>
        <select value={fields.originUserId} onChange={(e) => setFields({ ...fields, originUserId: e.target.value })} disabled={readOnly}>
          <option value="">—</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
        <label>{t('category')} :</label>
        <select value={fields.categoryId} onChange={(e) => setFields({ ...fields, categoryId: e.target.value })} disabled={readOnly}>
          <option value="">—</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <label className="req">{t('bulletin')} :</label>
        {id || rundownId ? <span className={placements.length ? undefined : 'muted'}>{placements.length ? placements.map((p) => p.programmeName).join(', ') : id ? '—' : '(από τον Οδηγό)'}</span> : (
          <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
            <option value="">{t('choose')}</option>
            {programmes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        <label className="req">{t('title')} :</label>
        <input className="wide" value={fields.title} onChange={(e) => setFields({ ...fields, title: e.target.value })} maxLength={500} disabled={readOnly} />

        <label>{t('storyNumber')} :</label>
        <input value={fields.displayNumber} onChange={(e) => setFields({ ...fields, displayNumber: e.target.value })} maxLength={20} disabled={readOnly} style={{ width: 80 }} />
        <label>{t('guideComments')} :</label>
        <input className="wide" value={fields.guideComments} onChange={(e) => setFields({ ...fields, guideComments: e.target.value })} maxLength={2000} disabled={readOnly} />

        <label>{t('videoTime')} :</label>
        <input value={fields.videoS} onChange={(e) => setFields({ ...fields, videoS: e.target.value })} placeholder="mm:ss" disabled={readOnly} style={{ width: 80 }} />
        <label>{t('readOverride')} :</label>
        <input value={fields.readOverrideS} onChange={(e) => setFields({ ...fields, readOverrideS: e.target.value })} placeholder={`${t('computed')} ${mmss(computedRead)}`} disabled={readOnly} style={{ width: 110 }} />
        <label>{t('itemType')} :</label>
        <select value={fields.kind} onChange={(e) => setFields({ ...fields, kind: e.target.value })} disabled={readOnly}>
          {ITEM_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </div>

      {chooser && (
        <MediaDialog
          item={{ title: fields.title, mediaAssetId: chooser.cue?.mediaAssetId ?? null }}
          currentTitle={chooser.cue?.media?.title ?? null}
          onClose={() => setChooser(null)}
          onAssign={assignClip}
        />
      )}
      {preview?.mediaAssetId && (
        <PreviewDialog
          mediaAssetId={preview.mediaAssetId}
          title={preview.media?.title ?? preview.mediaAssetId}
          meta={preview.media ? `${preview.media.filename} · ${hhmmss(Math.round(preview.media.durationMs / 1000))} · ${preview.media.status}` : ''}
          edit={preview.edit}
          cut={preview}
          onClose={() => setPreview(null)}
        />
      )}
      <div className="editor-toolbar">
        <span className="sl-seg">
          <button type="button" className={mode === 'storyline' ? 'on' : ''} onClick={() => switchMode('storyline')} title={t('slHint')}>
            📋 {t('slStoryline')}
          </button>
          <button type="button" className={mode === 'text' ? 'on' : ''} onClick={() => switchMode('text')} title={t('slTextHint')}>
            ¶ {t('slText')}
          </button>
        </span>
        {mode === 'text' && editor && (
          <>
            <button className={editor.isActive('bold') ? 'on' : ''} onClick={() => editor.chain().focus().toggleBold().run()} title="Ctrl+B">
              <b>B</b>
            </button>
            <button className={editor.isActive('italic') ? 'on' : ''} onClick={() => editor.chain().focus().toggleItalic().run()} title="Ctrl+I">
              <i>I</i>
            </button>
            <button className={editor.isActive('underline') ? 'on' : ''} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Ctrl+U">
              <u>U</u>
            </button>
            <button className={editor.isActive('strike') ? 'on' : ''} onClick={() => editor.chain().focus().toggleStrike().run()}>
              <s>abc</s>
            </button>
            <button className={editor.isActive('bulletList') ? 'on' : ''} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              • list
            </button>
            <button className={editor.isActive('orderedList') ? 'on' : ''} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              1. list
            </button>
            <button onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>Tx</button>
            <button onClick={() => editor.chain().focus().undo().run()}>↶</button>
            <button onClick={() => editor.chain().focus().redo().run()}>↷</button>
            <button
              onClick={() => {
                const q = window.prompt('Αναζήτηση λέξης στο κείμενο');
                if (q) (window as unknown as { find?: (s: string) => boolean }).find?.(q);
              }}
            >
              🔍
            </button>
          </>
        )}
        <button className={sbOpen ? 'on' : ''} onClick={toggleStoryboard} title={t('storyboardHint')} type="button">
          🎬 {t('storyboard')}
          {cues.length ? ` (${cues.length})` : ''}
        </button>
        <button className={spellOn ? 'on' : ''} onClick={toggleSpell} title={t('spellToggle')} type="button">
          ✓ {t('spell')}
          {spellOn && spellCount > 0 ? ` (${spellCount})` : ''}
        </button>
        <button className={aiOpen ? 'on' : ''} onClick={() => setAiOpen((o) => !o)} title={ai?.enabled ? t('aiPanel') : t('aiDisabled')} type="button">
          ✦ AI
        </button>
        <span style={{ marginLeft: 'auto' }} className="muted">
          {wordCount} {t('words')} · {t('readTime')} {mmss(fields.readOverrideS ? (parseMmSs(fields.readOverrideS) ?? 0) : computedRead)}
        </span>
      </div>
      <div className={aiOpen || sbOpen ? 'editor-with-panel' : undefined}>
        {mode === 'storyline' && <StorylinePanel blocks={blocks} cues={cues} storylineAt={storylineAt} locale={locale} readOnly={readOnly} busy={sbBusy} onChange={updateBlocks} onCue={onStorylineCue} onFlush={flushStorylineCg} />}
        <div className="editor-body" style={mode === 'storyline' ? { display: 'none' } : undefined}>
          <EditorContent editor={editor} />
        </div>
        {sbOpen && (
          <StoryboardPanel
            cues={cues}
            slots={slots}
            locale={locale}
            readOnly={readOnly}
            busy={sbBusy}
            notice={sbNotice}
            focusId={sbFocus}
            hasStory={!!savedStoryId}
            onCommit={(next) => void commitCues(next)}
            onChoose={(cue) => setChooser({ cue, slot: null })}
            onPreview={(cue) => setPreview(cue)}
            onClose={toggleStoryboard}
          />
        )}
        {aiOpen && (
          <AiPanel
            getText={editorPlainText}
            title={fields.title}
            readOnly={readOnly}
            onApplyText={applyAiText}
            onApplyTitle={(x) => setFields({ ...fields, title: x })}
            onClose={() => setAiOpen(false)}
          />
        )}
      </div>
      {spellPopup && editor && (
        <div className="spell-popup" style={{ left: Math.max(8, Math.min(spellPopup.left, window.innerWidth - 330)), top: spellPopup.bottom + 4 }}>
          <div className="word">{spellPopup.range.word}</div>
          {spellPopup.range.suggestions.length === 0 && (
            <div className="muted" style={{ padding: '2px 8px' }}>
              {t('spellNoSuggestions')}
            </div>
          )}
          {spellPopup.range.suggestions.map((w) => (
            <button key={w} type="button" disabled={readOnly} onClick={() => replaceWord(spellPopup.range, w)}>
              {w}
            </button>
          ))}
          <div className="sep" />
          <button type="button" onClick={() => void addToDictionary(spellPopup.range.word)}>
            ＋ {t('spellAddWord')}
          </button>
          <button type="button" onClick={() => ignoreWord(spellPopup.range.word)}>
            {t('spellIgnore')}
          </button>
        </div>
      )}
      <div className="savestate">
        <span>
          <span className={`dot ${saveState}`} /> {stateLabel[saveState]}
          {saveState === 'saved' && savedAt ? ` ${fmtDateTime(savedAt)}` : ''}
        </span>
        {story && (
          <>
            <span className="savestate-status">
              <StoryStatusBadge status={story.status} />
              {story.status === 'draft' && <span className="muted">{t('draftAutosaveNote')}</span>}
              {story.status === 'ready' && !readOnly && (
                <button type="button" className="btn small" onClick={() => void setStatus('draft')} title={t('backToDraftHint')}>
                  ↩ {t('backToDraft')}
                </button>
              )}
              {story.status === 'approved' && approver && (
                <button type="button" className="btn small" onClick={() => void setStatus('ready')}>
                  ↩ {t('unapprove')}
                </button>
              )}
              {statusNote && <span className="status warn">{statusNote}</span>}
            </span>
            <span>
              <span className="dot published" /> {t('revision')} {story.revision}
            </span>
            <span className="muted">
              {t('createdAt')}: {fmtDateTime(story.createdAt)} · {t('createdBy')}: {meta?.createdBy ?? '—'} · {t('updatedAt')}: {fmtDateTime(story.updatedAt)} · {t('updatedBy')}: {meta?.updatedBy ?? '—'}
            </span>
          </>
        )}
        {error && <span className="status err">{error}</span>}
      </div>
      {toolbar}
      <p className="tip">{t('tipRequiredFields')}</p>
      {savedPop > 0 && (
        <div key={savedPop} className="saved-pop" role="status">
          ✓ {t('savedPopup')}
        </div>
      )}
    </StrapDesignContext.Provider>
  );
}

interface Draft {
  html: string;
  revision: number;
  at: number;
}
function writeDraft(id: string, d: Draft) {
  try {
    localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(d));
  } catch {
    /* storage full or blocked: the server copy is the fallback */
  }
}
function readDraft(id: string): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + id);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}
function clearDraft(id: string) {
  try {
    localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    /* ignore */
  }
}
